import { engineComponent, ArrayUtils } from "neo-common";
import { BaseController } from "./BaseController";
import { Paylines } from "../Paylines";
import { PaylinesSelector } from "../PaylinesSelector";
import { Symbols } from "../Symbols";
import { WinAnimator } from "../WinAnimator";
import { Machine } from "../Machine";
import { ContainerElement } from "../../Components/Elements/Graphics/ContainerElement";
import { ComponentTypes } from "../../Components/ComponentTypes";
import { GameManager } from "../../Managers/GameManager";
import { FrameTimerEventEmitter, CreateFrameTimer, FrameTimerEvents } from "../../Utils/FrameTimer";
import { PlayList } from "../../Components/Sounds/PlayList";
import { DataManager } from "../../Managers/DataManager";
import { Tweens } from "../../Components/Effects/Tweens";
import { WinAnimatorExtended } from "../../CustomComponents/WinAnimatorExtended";
import { MachineExtended } from "../../CustomComponents/MachineExtended";
import { ImageElement } from "../../Components/Elements/Graphics/ImageElement";
import { StageController } from "../../Components/StageController";
import { ModuleManager } from "../../Managers/ModuleManager";
import { SwitchElement } from "../../Components/Elements/Graphics/SwitchElement";
import { TeasingBonus } from "../../CustomComponents/TeasingBonus";
import { BaseElement } from "../../Components/Elements/Graphics/BaseElement";
import { CustomEase } from "../../Components/Effects/CustomEase";
import { ReelTeasingActions } from "../../CustomComponents/TeasingComponentAbstract";

@engineComponent(ComponentTypes.DisplayObject)
export class BaseView extends ContainerElement {
    private _controller: BaseController;
    private _wasFinished: boolean = false;

    private _introDelay: FrameTimerEventEmitter;
    private _outroDelay: FrameTimerEventEmitter;
    private _machineStoppedTimer: FrameTimerEventEmitter;
    private _showPossibleWinsTimer: FrameTimerEventEmitter;

    private _paylines: Paylines;
    private _paylinesSelector: PaylinesSelector;

    private _symbols: Symbols;
    private _machine: MachineExtended;
    private _noWinSymbolsContainer: ContainerElement;
    private _symbolsContainer: ContainerElement;
    private _currentWinSymbolsContainer: ContainerElement;
    private _winAnimator: WinAnimatorExtended;

    private _logo: SwitchElement;

    public static Logo: SwitchElement;
    public static Background: SwitchElement;
    public static Foreground: SwitchElement;
    public static Speaker: SwitchElement;
    public static Rooster: SwitchElement;
    public static ReelLights: Array<SwitchElement> = [];
    public static Butterflies: Array<SwitchElement> = [];

    public static PossibleSymbolsOnReel: Array<Array<number>>;

    private _overlay: ImageElement;

    private _winAnimations: ContainerElement;

    private _teasingReel: SwitchElement | ContainerElement;
    private _teasingBonus: TeasingBonus;
    private inTeasing = false;
    private _spinBitmask: number;

    constructor() {
        super();

        this._introDelay = CreateFrameTimer("introDelay");
        this._introDelay.on(FrameTimerEvents.Complete, this.introFinished.bind(this));

        this._outroDelay = CreateFrameTimer("outroDelay");
        this._outroDelay.on(FrameTimerEvents.Complete, this.outroFinished.bind(this));

        this._machineStoppedTimer = CreateFrameTimer("machineStoppedTimer");
        this._machineStoppedTimer.on(FrameTimerEvents.Complete, this.machineStoppedDelayed.bind(this));

        this._showPossibleWinsTimer = CreateFrameTimer("showPossibleWinsTimer");

        this._teasingBonus = new TeasingBonus();

        StageController.baseStageContainer = this;
    }

    createByNode(node: Element): void {
        super.createByNode(node);

        if (this.childs != null) {
            for (let key in this.childs) {
                if (this.childs[key] instanceof Paylines) {
                    this._paylines = this.childs[key];
                }

                if (this.childs[key] instanceof PaylinesSelector) {
                    this._paylinesSelector = this.childs[key];
                }

                if (key === "Symbols" && this.childs[key] instanceof Symbols) {
                    this._symbols = this.childs[key];
                    this.removeChild(this._symbols);
                }

                if (this.childs[key] instanceof MachineExtended) {
                    this._machine = this.childs[key];
                }

                if (this.childs[key] instanceof WinAnimatorExtended) {
                    this._winAnimator = this.childs[key];
                }

                if (key === "NoWinSymbolsContainer" && this.childs[key] instanceof ContainerElement) {
                    this._noWinSymbolsContainer = this.childs[key];
                }

                if (key === "SymbolsContainer" && this.childs[key] instanceof ContainerElement) {
                    this._symbolsContainer = this.childs[key];
                }

                if (key === "CurrentWinSymbolsContainer" && this.childs[key] instanceof ContainerElement) {
                    this._currentWinSymbolsContainer = this.childs[key];
                }

                if (key === "Logo" && this.childs[key] instanceof SwitchElement) {
                    //this._logo = this.childs[key];
                    BaseView.Logo = this.childs[key];
                }

                if (key === "WinAnimations" && this.childs[key] instanceof ContainerElement) {
                    this._winAnimations = this.childs[key];
                }

                if (key === "Background" && this.childs[key] instanceof SwitchElement) {
                    BaseView.Background = this.childs[key];

                    if (BaseView.Background != null) {
                        let backgroundChilds = <any>BaseView.Background.getAllChildElements();
                        for (let key2 in backgroundChilds) {
                            if (key2.indexOf("ReelLight_") !== -1) {
                                BaseView.ReelLights.push(backgroundChilds[key2]);
                            } else if (key2.indexOf("Butterfly_") !== -1) {
                                BaseView.Butterflies.push(backgroundChilds[key2]);
                            }
                        }
                    }
                }

                if (key === "Foreground" && this.childs[key] instanceof SwitchElement) {
                    BaseView.Foreground = this.childs[key];
                }

                if (key === "Overlay" && this.childs[key] instanceof ImageElement) {
                    this._overlay = this.childs[key];
                }

                if (key === "Speaker" && this.childs[key] instanceof SwitchElement) {
                    BaseView.Speaker = this.childs[key];
                }

                if (key === "Rooster" && this.childs[key] instanceof SwitchElement) {
                    BaseView.Rooster = this.childs[key];
                }

                if (
                    key === "TeasingReel" &&
                    (this.childs[key] instanceof SwitchElement || this.childs[key] instanceof ContainerElement)
                ) {
                    this._teasingReel = this.childs[key];
                    if (this._teasingBonus != null) {
                        this._teasingBonus.addElementInstance({ teasingReel: this._teasingReel });
                        this._teasingBonus.eventEmitter.on(
                            this._teasingBonus.eventTeasing,
                            this.teasingBonusTeasing.bind(this),
                        );
                        this._teasingBonus.eventEmitter.on(
                            this._teasingBonus.eventTeasingWin,
                            this.teasingBonusTeasingWin.bind(this),
                        );
                        this._teasingBonus.eventEmitter.on(
                            this._teasingBonus.eventTeasingFail,
                            this.teasingBonusTeasingFail.bind(this),
                        );
                        this._teasingBonus.eventEmitter.on(
                            this._teasingBonus.eventTeasingReel,
                            this.animateTeasingOnReel.bind(this),
                        );
                    }
                }
            }

            if (DEBUG) {
                if (this._paylines == null) {
                    this.log("WARNING no paylines");
                }

                if (this._symbols == null) {
                    this.log("ERROR no symbols");
                }

                if (this._machine == null) {
                    this.log("ERROR no machine");
                }

                if (this._winAnimator == null) {
                    this.log("WARNING no winAnimator");
                }
            }
        }

        //create own custom easing functions we used in game
        CustomEase._createOwnEasing();
    }

    disableLogo(): void {
        if (this._logo != null) {
            this.removeChild(this._logo);
        }
    }

    initReceived(controller: BaseController): void {
        if (DEBUG) {
            this.log("initReceived");
        }

        this._controller = controller;

        if (this._symbols != null) {
            if (this._machine != null) {
                if (GameManager.baseResponse != null) {
                    if (GameManager.baseResponse.initData != null && GameManager.baseResponse.initData.reels != null) {
                        this._machine.on(Machine.ReelStopped, this.onReelStopped.bind(this));
                        this._machine.on(Machine.MachineStopped, this.machineStopped.bind(this));
                        if (this._teasingBonus != null) {
                            this._machine.addElementInstance({ teasing: [this._teasingBonus] });
                        }
                        if (this._teasingReel) {
                            this._machine.addElementInstance({ teasingReel: this._teasingReel });
                        }

                        this._machine.initialize(this._symbols, GameManager.baseResponse.initData.reels);
                    }

                    if (
                        GameManager.baseResponse.screen != null &&
                        GameManager.baseResponse.screen.reels != null &&
                        GameManager.baseResponse.screen.reels.length !== 0
                    ) {
                        if (this._winAnimator != null) {
                            this._winAnimator.on(
                                WinAnimator.FinishWinCalculation,
                                this.finishWinCalculation.bind(this),
                            );
                            this._winAnimator.on(WinAnimator.FinishWinAnimator, this.finishWinAnimator.bind(this));
                            this._winAnimator.on(
                                WinAnimator.FinishQuickWinCalculation,
                                this.finishQuickWinCalculation.bind(this),
                            );
                            if (this._teasingBonus != null) {
                                this._winAnimator.addElementInstance({ teasing: [this._teasingBonus] });
                            }

                            if (this._winAnimations != null) {
                                this._winAnimator.addElementInstance({ WinAnimations: this._winAnimations });
                            }
                            if (this._logo != null) {
                                this._winAnimator.addElementInstance({ Logo: this._logo });
                            }
                            if (this._overlay != null) {
                                this._winAnimator.addElementInstance({ Overlay: this._overlay });
                            }
                            if (BaseView.Speaker != null) {
                                this._winAnimator.addElementInstance({ Speaker: BaseView.Speaker });
                            }
                            if (BaseView.Rooster != null) {
                                this._winAnimator.addElementInstance({ Rooster: BaseView.Rooster });
                            }
                            if (BaseView.Butterflies != null) {
                                this._winAnimator.addElementInstance({ Butterflies: BaseView.Butterflies });
                            }
                            if (this._currentWinSymbolsContainer != null) {
                                this._winAnimator.addElementInstance({
                                    CurrentWinSymbolsContainer: this._currentWinSymbolsContainer,
                                });
                            }

                            this._winAnimator.initialize(
                                this._symbols,
                                this._paylines,
                                this.childs["NoWinSymbolsContainer"],
                                this.childs["SymbolsContainer"],
                            );
                            this._winAnimator.showSymbols(GameManager.baseResponse.screen.reels);
                        }

                        this._machine.setStoppedSymbolsArray(GameManager.baseResponse.screen.reels);
                    } else {
                        if (DEBUG) {
                            this.log("ERROR no reels in response");
                        }
                    }
                } else {
                    if (DEBUG) {
                        this.log("ERROR no base reponse");
                    }
                }
            } else {
                if (DEBUG) {
                    this.log("ERROR no machine");
                }
            }
        } else {
            if (DEBUG) {
                this.log("ERROR no symbols");
            }
        }

        this.toggleElements(false);
    }

    toggleElements(visible: boolean, long: boolean = false): void {
        if (long) {
            if (visible) {
                if (this._noWinSymbolsContainer != null) {
                    this._noWinSymbolsContainer.visible = true;
                    Tweens.removeTweens(this._noWinSymbolsContainer, "all");
                    Tweens.alphaTween(this._noWinSymbolsContainer, {
                        alphas: [0, 1],
                        tweenDuration: 15,
                    });
                }
                if (this._symbolsContainer != null) {
                    this._symbolsContainer.visible = true;
                    Tweens.removeTweens(this._symbolsContainer, "all");
                    Tweens.alphaTween(this._symbolsContainer, {
                        alphas: [0, 1],
                        tweenDuration: 15,
                    });
                }
                if (this._paylines != null) {
                    this._paylines.visible = true;
                    Tweens.removeTweens(this._paylines, "all");
                    Tweens.alphaTween(this._paylines, {
                        alphas: [0, 1],
                        tweenDuration: 15,
                    });
                }
                if (this._winAnimator != null) {
                    this._winAnimator.visible = true;
                    Tweens.removeTweens(this._winAnimator, "all");
                    Tweens.alphaTween(this._winAnimator, {
                        alphas: [0, 1],
                        tweenDuration: 15,
                    });
                }
                if (BaseView.Rooster != null && BaseView.Rooster instanceof SwitchElement) {
                    BaseView.Rooster.goToCase("BASE");
                }
            } else {
                if (this._noWinSymbolsContainer != null) {
                    this._noWinSymbolsContainer.alpha = 0;
                }
                if (this._symbolsContainer != null) {
                    this._symbolsContainer.alpha = 0;
                }
                if (this._paylines != null) {
                    this._paylines.alpha = 0;
                }
                if (this._winAnimator != null) {
                    this._winAnimator.alpha = 0;
                }

                if (BaseView.Rooster != null && BaseView.Rooster instanceof SwitchElement) {
                    BaseView.Rooster.goToCase("BASE");
                }
            }
        } else {
            if (this._noWinSymbolsContainer != null) {
                this._noWinSymbolsContainer.visible = visible;
            }
            if (this._symbolsContainer != null) {
                this._symbolsContainer.visible = visible;
            }
            if (this._paylines != null) {
                this._paylines.visible = visible;
            }
            if (this._winAnimator != null) {
                this._winAnimator.visible = visible;
            }

            //if (visible) {
            if (BaseView.Rooster != null && BaseView.Rooster instanceof SwitchElement) {
                BaseView.Rooster.goToCase("BASE");
            }
            //}
        }
    }

    swapChilds(src: BaseElement, childName: string, dest: any): void {
        if (src != null && dest.childs[childName] != null) {
            dest.addChildAt(src, dest.getChildIndex(dest.childs[childName]));
        }
    }

    teasingBonusTeasing(): void {
        if (!this.inTeasing) {
            this.inTeasing = true;

            PlayList.stop("SND_BaseLoop");
            if (this.enabledRespin()) {
                PlayList.play("SND_BaseTeaser01", 0, false);
                PlayList.play("SND_BaseRooster01", 0, false);
            } else {
                PlayList.play("SND_BaseTeaser02", 0, false);
                PlayList.play("SND_BaseRooster05", 0, false);
            }
            BaseView.Rooster.goToCase("TEASING");
        }
    }

    teasingBonusTeasingWin(): void {
        PlayList.stop("SND_BaseTeaser01", 30);
        PlayList.stop("SND_BaseTeaser02", 30);
        PlayList.loop("SND_BaseLoop");
        if (!this.enabledRespin()) {
            PlayList.play("SND_BaseTeaserWin");
        } else {
            PlayList.play("SND_BaseRooster07", 0, false);
            BaseView.Rooster.goToCase("TEASING_WIN");
        }

        for (let i: number = 0; i < GameManager.baseResponse.screen.reels.length; i++) {
            if (this._teasingBonus.reelsTeasingAttributes[i].action == ReelTeasingActions.TEASE) {
                if (this._teasingReel != null && this._teasingReel instanceof ContainerElement) {
                    this._teasingReel.childs["Teasing" + i].goToCase("STOP");
                }
            }
        }
    }

    teasingBonusTeasingFail(): void {
        PlayList.stop("SND_BaseTeaser01", 30);
        PlayList.stop("SND_BaseTeaser02", 30);
        PlayList.play("SND_BaseTeaser01Fail");
        if (!this.enabledRespin()) {
            PlayList.play("SND_BaseRooster02", 0, false);
        }
        PlayList.loop("SND_BaseLoop");
        BaseView.Rooster.goToCase("TEASING_FAIL");

        for (let i: number = 0; i < GameManager.baseResponse.screen.reels.length; i++) {
            if (this._teasingBonus.reelsTeasingAttributes[i].action == ReelTeasingActions.TEASE) {
                if (this._teasingReel != null && this._teasingReel instanceof ContainerElement) {
                    this._teasingReel.childs["Teasing" + i].goToCase("STOP");
                }
            }
        }
    }

    public animateTeasingOnReel(reelIndex: number): void {
        if (this._teasingBonus.teasingEnabled) {
            for (let i: number = 0; i < GameManager.baseResponse.screen.reels.length; i++) {
                if (this._teasingBonus.reelsTeasingAttributes[i].action == ReelTeasingActions.TEASE) {
                    if (i == reelIndex) {
                        if (this._teasingReel != null && this._teasingReel instanceof ContainerElement) {
                            this._teasingReel.childs["Teasing" + i].goToCase("START");
                        }
                    } else if (i < reelIndex) {
                        if (this._teasingReel != null && this._teasingReel instanceof ContainerElement) {
                            this._teasingReel.childs["Teasing" + i].goToCase("STOPPING");
                        }
                    }
                }
            }
        }
    }

    initIntro(): void {
        StageController.baseStage = true;

        this.swapChilds(BaseView.Logo, "SwapLogo", this);
        this.swapChilds(BaseView.Background, "SwapBackground", this);
        this.swapChilds(BaseView.Foreground, "SwapForeground", this);
        this.swapChilds(BaseView.Rooster, "SwapRooster", this);
        this.swapChilds(BaseView.Speaker, "SwapSpeaker", this);

        //if (GameManager.baseResponse != null && GameManager.baseResponse.totalWin > 0) {
        if (GameManager.gameResponse != null && GameManager.gameResponse.totalWin > 0) {
            PlayList.loop("SND_BaseRecapLoop");
        } else {
            PlayList.loop("SND_BaseLoop");
        }

        this.toggleElements(true);

        if (this.enabledRespin()) {
            if (BaseView.Logo != null && BaseView.Logo instanceof SwitchElement) {
                BaseView.Logo.goToCase("RESPIN");
            }
        } else {
            BaseView.PossibleSymbolsOnReel = this._winAnimator.resetAndReturnPossibleSymbolsOnReel();
            if (BaseView.Logo != null && BaseView.Logo instanceof SwitchElement) {
                BaseView.Logo.goToCase("NORMAL");
            }
        }

        this._winAnimator.showInitWins(GameManager.baseResponse.wins, GameManager.baseResponse.currentWin);
    }

    collect(): void {
        PlayList.stop("SND_BaseRecapLoop");
        PlayList.loop("SND_BaseLoop");

        if (this._paylinesSelector != null) {
            this._paylinesSelector.reset();
        }
        this._winAnimator.reset(true);
    }

    spin(spinBitmask: number): void {
        StageController.baseStage = true;

        PlayList.stop("SND_BaseRecapLoop");
        PlayList.loop("SND_BaseLoop");

        if (this._paylinesSelector != null) {
            this._paylinesSelector.reset();
        }

        this._machine.spinStart(
            this._winAnimator.reset(true, true, spinBitmask),
            true,
            this._winAnimator.currentSymbols,
            spinBitmask,
        );
    }

    respin(spinBitmask: number): void {
        StageController.baseStage = true;

        PlayList.stop("SND_BaseRecapLoop");
        PlayList.loop("SND_BaseLoop");

        if (this._paylinesSelector != null) {
            this._paylinesSelector.reset();
        }
        this._spinBitmask = spinBitmask;
        this._machine.respinStart(
            this._winAnimator.reset(true, true, spinBitmask),
            true,
            this._winAnimator.currentSymbols,
            spinBitmask,
        );
    }

    stop(): void {
        this._machine.spinStop(null, true);
    }

    spinReceived(): void {
        this._machine.setStoppedSymbolsArray(GameManager.baseResponse.screen.reels);
        this._machine.spinStop(null, false, true);
    }

    respinReceived(): void {
        this._machine.setStoppedSymbolsArray(GameManager.baseResponse.screen.reels);
        this._machine.spinStop(null, false, true);
        this._winAnimator.setSymbolsTeasingAttributes(this._spinBitmask);
    }

    onReelStopped(): void {
        this._winAnimator.onReelStopped(
            GameManager.baseResponse.screen.reels,
            this._machine.actualReelStopped,
            false,
            this._spinBitmask,
        );

        if (!this.enabledRespin()) {
            this._winAnimator.highlightPossibleSymbolsOnReelsByIds(
                this._spinBitmask,
                BaseView.PossibleSymbolsOnReel[this._machine.actualReelStopped],
                this._machine.actualReelStopped,
            );
        }
    }

    machineStopped(): void {
        this.inTeasing = false;
        BaseView.PossibleSymbolsOnReel = this._winAnimator.resetAndReturnPossibleSymbolsOnReel();
        this._spinBitmask = 31;

        if (this.enabledRespin()) {
            if (BaseView.Logo != null && BaseView.Logo instanceof SwitchElement) {
                BaseView.Logo.goToCase("RESPIN");
            }
        } else {
            if (BaseView.Logo != null && BaseView.Logo instanceof SwitchElement) {
                BaseView.Logo.goToCase("NORMAL");
            }
        }

        for (let i: number = 0; i < GameManager.baseResponse.screen.reels.length; i++) {
            if (this._teasingReel != null && this._teasingReel instanceof ContainerElement) {
                this._teasingReel.childs["Teasing" + i].goToCase("STOP");
            }
        }

        this._machineStoppedTimer.start(15);

        PlayList.stop("SND_BaseTeaser01Fail", 10);
        PlayList.stop("SND_BaseTeaser02Fail", 10);
        PlayList.stop("SND_BaseTeaser01", 10);
        PlayList.stop("SND_BaseTeaser02", 10);
    }

    protected machineStoppedDelayed(): void {
        this._machineStoppedTimer.stop();

        this._wasFinished = false;
        this._controller.machineStopped();

        if (!this.enabledRespin()) {
            if (this._winAnimator.showWins(GameManager.baseResponse.wins, GameManager.baseResponse.currentWin)) {
                PlayList.stop("SND_BaseLoop");
            }
        }
    }

    private finishQuickWinCalculation(): void {
        this._controller.quickWinCalculationFinished();
    }

    finishWinCalculation(): void {
        this._wasFinished = true;
        this._controller.winCalculationFinished();
    }

    finishWinAnimator(): void {
        if (!DataManager.Autoplay) {
            PlayList.stop("SND_BaseLoop");
            PlayList.loop("SND_BaseRecapLoop");
        }

        this._controller.allWinsFinished();
    }

    resetWinAnimator(): void {
        this._winAnimator.reset(true);
    }

    intro(long: boolean = false): void {
        StageController.baseStage = true;

        this.swapChilds(BaseView.Logo, "SwapLogo", this);
        this.swapChilds(BaseView.Background, "SwapBackground", this);
        this.swapChilds(BaseView.Foreground, "SwapForeground", this);
        this.swapChilds(BaseView.Rooster, "SwapRooster", this);
        this.swapChilds(BaseView.Speaker, "SwapSpeaker", this);

        this.toggleElements(true, true);

        if (BaseView.Logo != null && BaseView.Logo instanceof SwitchElement) {
            BaseView.Logo.goToCase("NORMAL");
        }

        if (BaseView.Background != null && BaseView.Background instanceof SwitchElement) {
            BaseView.Background.goToCase("BASE");
        }

        if (BaseView.Foreground != null && BaseView.Foreground instanceof SwitchElement) {
            BaseView.Foreground.goToCase("BASE");
        }

        if (long) {
            this._machine.setStoppedSymbolsArray(GameManager.baseResponse.screen.reels);

            this._winAnimator.showSymbols(GameManager.baseResponse.screen.reels);
            this._winAnimator.reset(false);
            this._winAnimator.showExceptBonusSymbols();
            //this._paylinesSelector.reset();

            this._introDelay.start(60);
        } else {
            this.introFinished();
        }

        BaseView.PossibleSymbolsOnReel = this._winAnimator.resetAndReturnPossibleSymbolsOnReel();
    }

    introFinished(): void {
        StageController.baseStage = true;

        if (GameManager.gameResponse.totalWin > 0) {
            PlayList.stop("SND_BaseLoop");
            PlayList.loop("SND_BaseRecapLoop");
        } else {
            PlayList.stop("SND_BaseRecapLoop");
            PlayList.loop("SND_BaseLoop");
        }

        this._controller.introFinished();
    }

    outro(): void {
        StageController.baseStage = true;

        PlayList.stop("SND_BaseLoop");
        PlayList.stop("SND_BaseRecapLoop");

        if (this._paylinesSelector != null) {
            this._paylinesSelector.reset();
        }

        const inHighWins = this._winAnimator.reset(true);
        this._winAnimator.resetTimer(); // mainly due to reset idle timer

        if (this._wasFinished) {
            if (ModuleManager.pickActive()) {
                PlayList.play("SND_BonusIntro");
                for (let i = 0; i <= 4; i++) {
                    this.holdReel(i, false);
                }

                this._winAnimator.showOnlyBonusSymbols();
                this._outroDelay.start(120);
            } else {
                this.outroFinished();
            }
        } else {
            if (inHighWins) {
                if (ModuleManager.pickActive()) {
                    PlayList.play("SND_BonusIntro");
                    for (let i = 0; i <= 4; i++) {
                        this.holdReel(i, false);
                    }

                    this._winAnimator.showOnlyBonusSymbols();
                    this._outroDelay.start(120);
                } else {
                    this._outroDelay.start(75);
                }
            } else {
                if (ModuleManager.pickActive()) {
                    PlayList.play("SND_BonusIntro");
                    for (let i = 0; i <= 4; i++) {
                        this.holdReel(i, false);
                    }

                    this._winAnimator.showOnlyBonusSymbols();
                    this._outroDelay.start(120);
                } else {
                    this._outroDelay.start(30);
                }
            }
        }
    }

    outroFinished(): void {
        if (ModuleManager.pickActive()) {
            PlayList.play("SND_BonusTrans");
        }
        this.toggleElements(false, true);
        StageController.baseStage = false;
        this._controller.outroFinished();
    }

    selectPayline(id?: number): void {
        if (this._paylinesSelector != null) {
            this._paylinesSelector.select(id);
        }
    }

    /**
     *
     * @param reelId
     * @param held
     * @param spinBitmask
     */
    holdReel(reelId: number, held: boolean, spinBitmask: number = 31): void {
        if (BaseView.Background != null) {
            let board: ImageElement = <ImageElement>(
                BaseView.Background.getChildElementByName("BuildingBoard_" + (reelId + 1))
            );
            if (board != null) {
                board.visible = true;
                if (held) {
                    board.alpha = 1;
                } else {
                    board.alpha = 0;
                }

                this._winAnimator.showShadowUnderReelSymbols(reelId, held);
            }
        }

        if (this.enabledRespin()) {
            /*this._showPossibleWinsTimer.once(FrameTimerEvents.Complete, this.showPossibleWinsOnHeldReels.bind(this, spinBitmask));
            this._showPossibleWinsTimer.start(5);*/

            let arr = this._winAnimator.showPossibleWinsOnHeldReels(spinBitmask);
            if (arr.hasOwnProperty("possibleSymbolsOnReel")) {
                BaseView.PossibleSymbolsOnReel = arr["possibleSymbolsOnReel"];
            }
        }
    }

    /**
     * @returns {boolean}
     */
    enabledRespin(): boolean {
        return (
            GameManager.baseResponse != null &&
            GameManager.baseResponse.respin != null &&
            GameManager.baseResponse.respin !== 0 &&
            GameManager.baseResponse.spinId === 0
        );
    }

    /**
     *
     * @returns {boolean}
     */
    activeRespin(): boolean {
        return (
            GameManager.baseResponse != null &&
            GameManager.baseResponse.respin !== 0 &&
            GameManager.baseResponse.spinId > 0
        );
    }

    /**
     * Indicates that last respin round has finished.
     * @returns {boolean}
     */
    lastRespin(): boolean {
        return (
            GameManager.baseResponse != null &&
            GameManager.baseResponse.respin != null &&
            GameManager.baseResponse.respin === 0 &&
            GameManager.baseResponse.spinId > 0
        );
    }

    disableBase(): void {
        this._winAnimator.resetTimer();
    }

    /**
     * Toggle all sounds used in base game (background, effects, .etc)
     * @param enable
     */
    toggleSounds(enable: boolean): void {
        if (enable) {
            PlayList.loop("SND_BaseLoop");
        } else {
            PlayList.stop("SND_BaseLoop");
            PlayList.stop("SND_BaseRecapLoop");

            this._winAnimator.resetTimer(); // due to reset idle timer

            if (BaseView.Rooster != null && BaseView.Rooster instanceof SwitchElement) {
                BaseView.Rooster.goToCase("BASE");
            }
        }
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/GameComponents/Base/BaseView.ts
 **/